import { CSSInterpolation } from '@emotion/css/create-instance'
import React, {
  createRef,
  PureComponent,
  type ReactNode,
  type RefObject,
} from 'react'

export const testIds = {
  Root: 'rh-design-Accordion-root',
}

export enum AccordionPhase {
  Stable = 'stable',
  Lock = 'lock',
  Animate = 'animate',
}

type Props = {
  children: ReactNode
  duration?: number
  isOpen: boolean
  /**
   * Called when the Accordion either begins to animate or is done animating.
   */
  onStateChange?: (
    isOpen: boolean,
    phase: AccordionPhase.Animate | AccordionPhase.Stable
  ) => void
  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation
}

// The accordion transitions between three phases
// - stable: The accordion is either totally open or totally closed
// - lock: The accordion is locked to its starting size. The element
//         needs to be set at that size for a short period of time so
//         that a CSS transition can animate from that size to the
//         end size.
// - animate: The accordion is currently animating toward its end size.

type State =
  | {
      phase: AccordionPhase.Stable
      isOpen: boolean
      duration: null
      size: null
    }
  | {
      phase: AccordionPhase.Lock | AccordionPhase.Animate
      isOpen: boolean
      duration: number
      size: number
    }

/**
 * Renders a component that hides and reveals its children.
 */
export class Accordion extends PureComponent<Props, State> {
  timeout?: number
  contentRef: RefObject<HTMLDivElement>

  constructor(props: Props) {
    super(props)
    this.contentRef = createRef<HTMLDivElement>() as RefObject<HTMLDivElement>
    this.state = {
      duration: null,
      isOpen: this.props.isOpen,
      phase: AccordionPhase.Stable,
      size: null,
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (!this.contentRef.current) {
      return
    }

    if (prevProps.isOpen !== this.props.isOpen) {
      if (
        // If the accordion was stable, then we're good to go. We should just
        // start the animation.
        this.state.phase === AccordionPhase.Stable ||
        // if we were interrupted in the middle of the "lock" phase,
        // we should start it over
        this.state.phase === AccordionPhase.Lock
      ) {
        const height = this.contentRef.current.getBoundingClientRect().height

        // it's possible that any currently running timeout could execute *just before*
        // our setState call actually gets propagated. This would lead to a stale state
        // update in that timeout getting enqueued right after this one
        clearTimeout(this.timeout)
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({
          duration:
            this.props.duration === undefined
              ? getDuration(height)
              : this.props.duration,
          isOpen: this.props.isOpen,
          phase: AccordionPhase.Lock,
          size: height,
        })
      } else if (this.state.phase === AccordionPhase.Animate) {
        // if we were interrupted in the middle of the "animate" phase,
        // we should start it over

        // it's possible that any currently running timeout could execute *just before*
        // our setState call actually gets propagated. This would lead to a stale state
        // update in that timeout getting enqueued right after this one
        clearTimeout(this.timeout)
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({
          isOpen: this.props.isOpen,
          phase: AccordionPhase.Animate,
        })
      }
    } else if (
      // we only want to bother dealing with starting a new animation
      // or interrupting an ongoing one if either the phase or open
      // status changed
      prevState.phase !== this.state.phase ||
      prevState.isOpen !== this.state.isOpen
    ) {
      if (this.state.phase === AccordionPhase.Lock) {
        // Just started the lock phase. Set a short timeout to give the measurement
        // styling time to get flushed to the DOM, then start the animate step
        clearTimeout(this.timeout)
        this.timeout = window.setTimeout(
          () => {
            this.setState({ phase: AccordionPhase.Animate })
          },
          // make sure we give the browser enough time in the lock step
          // to actually register the styles we applied
          50
        )
      } else if (this.state.phase === AccordionPhase.Animate) {
        // Just started the animate phase. Set a timeout that lasts the duration of
        // the animation, then mark the element as stable
        clearTimeout(this.timeout)
        const isOpen = this.state.isOpen
        this.timeout = window.setTimeout(() => {
          this.setState({
            duration: null,
            isOpen,
            phase: AccordionPhase.Stable,
            size: null,
          })
        }, this.state.duration)
      }
    }

    if (
      this.props.onStateChange &&
      (prevState.phase !== this.state.phase ||
        prevState.isOpen !== this.state.isOpen) &&
      this.state.phase !== AccordionPhase.Lock
    ) {
      this.props.onStateChange(this.state.isOpen, this.state.phase)
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timeout)
  }

  render() {
    const isFullyClosed =
      !this.state.isOpen && this.state.phase === AccordionPhase.Stable
    return (
      <div
        css={[getStyles(this.state), this.props.overrideRootStyles]}
        data-testid={testIds.Root}
      >
        <div
          ref={this.contentRef}
          aria-hidden={isFullyClosed ? 'true' : 'false'}
          css={[
            // If the outermost element of `this.props.children` has any vertical margin, we
            // want that margin to be accounted for in the result of
            // `this.contentRef.current.getBoundingClientRect()` so that the animation happens
            // smoothly. But because this `div` doesn't have any other content, the child's
            // margin will by default be collapsed past this parent and therefore the measured
            // height of this element will not include the height of that margin. The simplest
            // way to prevent this margin collapse, without modifying the resulting DOM, is to
            // ensure this `div` produces a new block formatting context. The best way to do this
            // without modifying any other visual behavior is to use `display: flow-root`. See
            // https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Box_Model/Mastering_margin_collapsing
            // and https://developer.mozilla.org/en-US/docs/Web/Guide/CSS/Block_formatting_context
            // for more details.
            { display: 'flow-root' },
            // When the accordion is fully closed, we set visibility to
            // hidden so that none of the content inside is focusable
            isFullyClosed && { visibility: 'hidden' },
          ]}
        >
          {this.props.children}
        </div>
      </div>
    )
  }
}

function getStyles(state: State): CSSInterpolation {
  if (state.phase === AccordionPhase.Lock) {
    return state.isOpen
      ? { height: 0, overflow: 'hidden', willChange: 'height' }
      : { height: `${state.size}px`, willChange: 'height' }
  }

  if (state.phase === AccordionPhase.Animate) {
    return {
      height: state.isOpen ? `${state.size}px` : 0,
      overflow: 'hidden',
      transition: `height ${state.duration}ms`,
      willChange: 'height',
    }
  }

  return state.isOpen
    ? { height: undefined, willChange: 'height' }
    : { height: 0, overflow: 'hidden', willChange: 'height' }
}

function getDuration(height: number) {
  return Math.max(300, Math.min(height, 1000))
}
